import {
	type AgentSession,
	type Artifact,
	type AuthSession,
	AuthSessionSchema,
	type ChatMessage,
	type ConversationFrame,
	type CreateAgentSessionRequest,
	type CreateArtifactRequest,
	type CreateProjectRequest,
	ErrorCodes,
	type EnsureGitRepositoryRequest,
	type EnsureGitRepositoryResponse,
	type GitRepository,
	type LoginRequest,
	type Page,
	PROGRESS_EVENT_SCHEMA_VERSION,
	type ProgressEvent,
	type Project,
	ReasonCodes,
	type SendChatMessageRequest,
	type SessionBootstrap,
	SessionBootstrapSchema,
	type SignupRequest,
	type StartExecutionRequest,
	StartExecutionRequestSchema,
	type Tenant,
	type TenantMembership,
	type User,
	type WorkflowExecution,
	type WorkOrder,
	type WorkOrderProgress,
	type WorkOrderSessionRole,
	type WorkOrderStatus,
	type WorkspaceTypeList,
} from "@agentry/zerovia-contracts";
import manifest from "@agentry/zerovia-contracts/manifest.json" with { type: "json" };
import type {
	ApprovalDecision,
	ApprovalRequest,
	AuthRouteView,
	Budget,
	ByoPolicy,
	CatalogPublication,
	ContactMessage,
	DownloadTicket,
	Integration,
	Notice,
	NotificationItem,
	OwnershipTransferRecord,
	PersonalCredential,
	Plan,
	PoolAssignment,
	ProductSettings,
	ProviderUsageRule,
	RoleDefinition,
	StorageObject,
	TenantCatalogEntitlement,
	TenantInvite,
	TenantMember,
	TenantSettings,
	TenantSummary,
	UsageSummary,
	UserAttributeMap,
	WorkspaceEntry,
} from "../agentry-types.js";
import { ApiError } from "../errors.js";
import type {
	ApiMode,
	AuditEventView,
	AuthProfileView,
	Backing,
	Catalog,
	CommandOptions,
	CostEventView,
	DashboardSummary,
	ExecutionTimeline,
	ListQuery,
	ProgressSubscription,
	ProjectListItemView,
	ProjectRepositoryConnection,
	ProviderView,
	Scope,
	SessionDetail,
	SubscribeOptions,
	WorkflowCommand,
	WorkOrderFrameView,
	WorkOrderProjectSettings,
	WorkOrderStreamOptions,
	ZeroviaClient,
} from "../types.js";
import * as seed from "./seed.js";
import {
	checksumOf,
	LocalStoragePersistence,
	MockStore,
	type StatePersistence,
	stableHash,
} from "./store.js";

const SESSION_KEY = "agentry.mock.auth.v1";
const DOWNLOAD_TICKET_TTL_MS = 5 * 60_000;

/** Zeroviaが実装済みの領域だけ ZEROVIA を返す。それ以外は MOCK_ONLY。 */
const ZEROVIA_BACKED = new Set<keyof ZeroviaClient>([
	"login",
	"signup",
	"me",
	"logout",
	"getUserAttributes",
	"upsertUserAttributes",
	"listOrganizationProjects",
	"listWorkspaceTypes",
	"createProject",
	"archiveProject",
	"restoreProject",
	"listOrganizationRepositories",
	"listProjectGitRepositories",
	"ensureProjectGitRepository",
	"listProjectRepositories",
	"connectProjectRepository",
	"disconnectProjectRepository",
	"getCatalog",
	"getDashboard",
	"listProviders",
	"listAuthProfiles",
	"listSessions",
	"createSession",
	"getSession",
	"sendChat",
	"submitUserInput",
	"getWorkOrderBoard",
	"getWorkOrderDetail",
	"getWorkOrderSessionFrames",
	"transitionWorkOrder",
	"deleteWorkOrder",
	"getWorkOrderProjectSettings",
	"updateWorkOrderProjectSettings",
	"retryWorkOrder",
	"restoreWorkOrder",
	"answerWorkOrderQuestion",
	"sendBoardDesignMessage",
	"sendWorkOrderSessionMessage",
	"subscribeDesignFrames",
	"subscribeWorkOrderFrames",
	"subscribeWorkOrderProgress",
	"listExecutions",
	"getTimeline",
	"sendWorkflowCommand",
	"subscribeProgress",
	"listArtifacts",
	"createArtifact",
	"getArtifact",
	"getArtifactContent",
	"createDownloadTicket",
	"listCostEvents",
	"listAuditEvents",
	"verifyContract",
]);

export type MockClientOptions = {
	persistence?: StatePersistence;
	/** Testで即時完了させるため。Browserでは既定の疑似遅延を使う。 */
	latencyMs?: number;
	/** 決定的な時刻。未指定ならDate.now()。 */
	now?: () => number;
};

export class MockZeroviaClient implements ZeroviaClient {
	readonly mode: ApiMode = "mock";
	private readonly store: MockStore;
	private readonly latency: number;
	private readonly now: () => number;
	private authed: AuthSession | null = null;

	constructor(options: MockClientOptions = {}) {
		this.store = new MockStore(options.persistence ?? new LocalStoragePersistence());
		this.latency = options.latencyMs ?? 120;
		this.now = options.now ?? (() => Date.now());
		this.authed = readStoredAuth();
	}

	backingOf(operation: keyof ZeroviaClient): Backing {
		return ZEROVIA_BACKED.has(operation) ? "ZEROVIA" : "MOCK_ONLY";
	}

	/** MockはPin値をそのまま返す。Realとの差異を隠さないため verified を明示する。 */
	async verifyContract() {
		return {
			contractVersion: manifest.contractVersion,
			contractHash: manifest.contractHash,
			verified: true,
		};
	}

	/**
	 * Real ClientがLoginしたSessionを引き継ぐ。
	 * Real modeでもMOCK_ONLYの操作（Tenant Admin / System Adminの一部）が
	 * 「未認証」で落ちないようにするための橋渡しであり、認証の検証はしない。
	 */
	adoptSession(session: AuthSession | null): void {
		this.authed = session;
	}

	resetState(): void {
		this.store.reset();
	}

	// -- helpers ------------------------------------------------------------

	private async delay(): Promise<void> {
		if (this.latency <= 0) return;
		await new Promise((resolve) => setTimeout(resolve, this.latency));
	}

	private iso(offsetMs = 0): string {
		return new Date(this.now() + offsetMs).toISOString();
	}

	/**
	 * Tenant境界の検証。別TenantのResourceは Forbidden ではなく Not Found とし、
	 * 存在そのものを漏らさない（design §24.9）。
	 */
	private assertScope(scope: Scope): void {
		if (!this.authed) {
			throw new ApiError({
				code: ErrorCodes.UNAUTHORIZED,
				message: "Not authenticated",
				reason: ReasonCodes.TOKEN_INVALID,
			});
		}
		if (
			this.authed.membership.role === "platform_admin" ||
			this.authed.membership.role === "platform_viewer"
		) {
			return;
		}
		if (this.authed.tenant.id !== scope.tenantId) {
			throw ApiError.notFound("Not found", ReasonCodes.TENANT_MISMATCH);
		}
	}

	private page<T>(items: T[], query?: ListQuery): Page<T> {
		const limit = query?.limit ?? 50;
		const offset = query?.offset ?? 0;
		return { items: items.slice(offset, offset + limit), total: items.length, limit, offset };
	}

	private audit(action: string, resourceType: string, resourceId: string, reason: string): void {
		this.store.mutate((state) => {
			state.auditEvents.unshift({
				id: `audit-${state.sequence++}`,
				actorId: this.authed?.user.id ?? "system",
				action,
				resourceType,
				resourceId,
				reason,
				traceId: `trace-${stableHash([action, resourceId, state.sequence])}`,
				createdAt: this.iso(),
			});
		});
	}

	// -- auth ---------------------------------------------------------------

	async login(input: LoginRequest): Promise<AuthSession> {
		await this.delay();
		const account = seed.MOCK_ACCOUNTS.find((a) => a.email === input.email);
		if (!account || account.password !== input.password) {
			throw new ApiError({
				code: ErrorCodes.UNAUTHORIZED,
				message: "メールアドレスまたはパスワードが正しくありません。",
				reason: ReasonCodes.INVALID_CREDENTIALS,
			});
		}
		return this.establishSession(account.userId);
	}

	async signup(input: SignupRequest): Promise<SessionBootstrap> {
		await this.delay();
		if (seed.MOCK_ACCOUNTS.some((a) => a.email === input.email)) {
			throw ApiError.conflict(
				"このメールアドレスは既に登録されています。",
				ReasonCodes.EMAIL_ALREADY_REGISTERED,
			);
		}
		const user: User = {
			id: this.store.nextId("user"),
			email: input.email,
			name: input.name,
			status: "ACTIVE",
			createdAt: this.iso(),
		};
		const membership: TenantMembership = {
			id: this.store.nextId("membership"),
			userId: user.id,
			tenantId: seed.TENANT_MAIN,
			role: "tenant_member",
			status: "ACTIVE",
			createdAt: this.iso(),
		};
		this.store.mutate((state) => {
			state.members.push({
				id: `member-${state.sequence++}`,
				tenantId: seed.TENANT_MAIN,
				email: user.email,
				name: user.name,
				role: "tenant_member",
				status: "ACTIVE",
				lastActiveAt: this.iso(),
				joinedAt: this.iso(),
			});
		});
		return this.persistAuth(user, seed.tenants[0] as Tenant, membership, seed.PROJECT_MAIN);
	}

	async me(): Promise<AuthSession | null> {
		return this.authed;
	}

	async logout(): Promise<void> {
		this.authed = null;
		try {
			localStorage?.removeItem(SESSION_KEY);
		} catch {
			/* storage unavailable — in-memory clear is enough */
		}
	}

	async getUserAttributes(userId: string): Promise<UserAttributeMap> {
		this.assertAttributeAccess(userId);
		await this.delay();
		return this.attributeMapOf(userId);
	}

	async upsertUserAttributes(
		userId: string,
		attributes: Record<string, string>,
	): Promise<UserAttributeMap> {
		this.assertAttributeAccess(userId, { write: true });
		await this.delay();
		const actorId = this.authed?.user.id ?? userId;
		const now = this.iso();
		this.store.mutate((state) => {
			for (const [key, value] of Object.entries(attributes)) {
				const existing = state.userAttributes.find(
					(item) => item.userId === userId && item.key === key,
				);
				if (existing) {
					existing.value = value;
					existing.updatedAt = now;
					existing.updatedBy = actorId;
				} else {
					state.userAttributes.push({
						userId,
						key,
						value,
						updatedAt: now,
						updatedBy: actorId,
					});
				}
			}
		});
		return this.attributeMapOf(userId);
	}

	private assertAttributeAccess(userId: string, options: { write?: boolean } = {}): void {
		if (!this.authed) {
			throw new ApiError({
				code: ErrorCodes.UNAUTHORIZED,
				message: "Not authenticated",
				reason: ReasonCodes.TOKEN_INVALID,
			});
		}
		const role = this.authed.membership.role;
		if (options.write && role === "platform_viewer") {
			throw ApiError.forbidden("Read-only role cannot write attributes");
		}
		if (this.authed.user.id === userId) return;
		if (role === "platform_admin" || role === "platform_viewer" || role === "tenant_admin") {
			return;
		}
		throw ApiError.notFound("User not found");
	}

	private attributeMapOf(userId: string): UserAttributeMap {
		return {
			userId,
			items: this.store
				.read()
				.userAttributes.filter((item) => item.userId === userId)
				.map(({ key, value, updatedAt, updatedBy }) => ({ key, value, updatedAt, updatedBy })),
		};
	}

	async requestPasswordReset(email: string): Promise<{ accepted: true }> {
		await this.delay();
		// 存在するかどうかを応答で区別しない（User enumeration防止）。
		this.audit("password_reset.request", "USER", email, "パスワード再設定を要求");
		return { accepted: true };
	}

	async acceptInvite(
		token: string,
		input: { name: string; password: string },
	): Promise<AuthSession> {
		await this.delay();
		if (input.password.length < 8) {
			throw ApiError.validation("パスワードは8文字以上にしてください。");
		}
		const invite = this.store.read().invites.find((i) => i.token === token);
		if (invite?.status !== "PENDING") {
			throw ApiError.notFound("招待が見つからないか、既に使用されています。");
		}
		this.store.mutate((state) => {
			const target = state.invites.find((i) => i.id === invite.id);
			if (target) target.status = "ACCEPTED";
			const member = state.members.find((m) => m.email === invite.email);
			if (member) {
				member.status = "ACTIVE";
				member.name = input.name;
			}
		});
		const user: User = {
			id: this.store.nextId("user"),
			email: invite.email,
			name: input.name,
			status: "ACTIVE",
			createdAt: this.iso(),
		};
		const membership: TenantMembership = {
			id: this.store.nextId("membership"),
			userId: user.id,
			tenantId: invite.tenantId,
			role: invite.role,
			status: "ACTIVE",
			createdAt: this.iso(),
		};
		const tenant =
			seed.tenants.find((x) => x.id === invite.tenantId) ?? (seed.tenants[0] as Tenant);
		return this.persistAuth(user, tenant, membership, seed.PROJECT_MAIN);
	}

	private establishSession(userId: string): SessionBootstrap {
		const user = seed.users.find((u) => u.id === userId);
		if (!user) throw ApiError.notFound("User not found");
		const membership = seed.memberships.find(
			(candidate) => candidate.userId === user.id && candidate.status === "ACTIVE",
		);
		if (!membership) throw ApiError.notFound("Membership not found");
		const tenant =
			seed.tenants.find((candidate) => candidate.id === membership.tenantId) ??
			(seed.tenants[0] as Tenant);
		const project = seed.projects.find((candidate) => candidate.tenantId === membership.tenantId);
		return this.persistAuth(user, tenant, membership, project?.id ?? seed.PROJECT_MAIN);
	}

	private persistAuth(
		user: User,
		tenant: Tenant,
		membership: TenantMembership,
		projectId: string,
	): SessionBootstrap {
		const project = seed.projects.find((candidate) => candidate.id === projectId);
		if (!project) throw ApiError.notFound("Project not found");
		const session: SessionBootstrap = {
			// Mockは検証可能なJWTを発行しない。Real modeと取り違えないよう明示する。
			accessToken: `mock-token.${user.id}`,
			expiresAt: this.iso(8 * 3600_000),
			refreshToken: `mock-refresh-token.${user.id}`,
			refreshExpiresAt: this.iso(30 * 24 * 3600_000),
			user,
			tenant,
			membership,
			project,
		};
		this.authed = session;
		try {
			localStorage?.setItem(SESSION_KEY, JSON.stringify(session));
		} catch {
			/* storage unavailable — session stays in memory for this tab */
		}
		return session;
	}

	// -- catalog / dashboard -------------------------------------------------

	async getCatalog(scope: Scope): Promise<Catalog> {
		this.assertScope(scope);
		await this.delay();
		return {
			agents: seed.agents,
			ucs: seed.ucsList,
			verticalPackages: seed.verticalPackages,
			workflowDefinitions: seed.workflowDefinitions,
		};
	}

	async getDashboard(scope: Scope): Promise<DashboardSummary> {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const executions = state.executions.filter((e) => e.tenantId === scope.tenantId);
		return {
			summary: {
				providers: seed.providers.filter((p) => p.status === "ACTIVE").length,
				sessions: state.sessions.filter((s) => s.tenantId === scope.tenantId).length,
				runningExecutions: executions.filter(
					(e) => e.status === "RUNNING" || e.status === "WAITING",
				).length,
				artifacts: state.artifacts.filter((a) => a.tenantId === scope.tenantId).length,
				cost: state.costEvents.reduce((sum, c) => sum + (c.amount ?? 0), 0),
			},
			recentExecutions: executions.slice(0, 5),
			recentAudit: state.auditEvents.slice(0, 5).map((a) => ({
				id: a.id,
				action: a.action,
				resourceType: a.resourceType,
				createdAt: a.createdAt,
			})),
		};
	}

	// -- provider / credential ----------------------------------------------

	async listProviders(scope: Scope): Promise<Page<ProviderView>> {
		this.assertScope(scope);
		await this.delay();
		return this.page(seed.providers);
	}

	async listAuthProfiles(scope: Scope): Promise<Page<AuthProfileView>> {
		this.assertScope(scope);
		await this.delay();
		const profiles: AuthProfileView[] = [
			{
				id: "auth-fake-system",
				providerId: "provider-fake",
				displayName: "Standard Assist",
				ownerType: "PLATFORM",
				status: "ACTIVE",
				secretRef: "fake:local",
				lastVerifiedAt: seed.T0,
			},
		];
		return this.page(profiles);
	}

	async listPersonalCredentials(scope: Scope): Promise<PersonalCredential[]> {
		this.assertScope(scope);
		await this.delay();
		const userId = this.authed?.user.id;
		return this.store.read().personalCredentials.filter((c) => c.userId === userId);
	}

	async createPersonalCredential(
		scope: Scope,
		input: {
			providerId: string;
			displayName: string;
			authMethod: PersonalCredential["authMethod"];
			secretRef: string;
		},
	): Promise<PersonalCredential> {
		this.assertScope(scope);
		await this.delay();

		// Secret本文は受け取らない。`env:` / `vault:` / `fake:` の参照のみ許可する。
		if (!/^(env|vault|fake):/.test(input.secretRef)) {
			throw ApiError.validation(
				"Secret本文は保存できません。env: / vault: / fake: 形式の参照を入力してください。",
				ReasonCodes.SECRET_LITERAL_REJECTED,
			);
		}
		const policy = this.store.read().byoPolicy;
		const wantsSubscription = input.authMethod === "SUBSCRIPTION_LOGIN";
		if (wantsSubscription && !policy.byosAllowed) {
			throw ApiError.forbidden(
				"Tenant AdminがSubscription認証（BYOS）を許可していません。",
				ReasonCodes.PROVIDER_POLICY_DENIED,
			);
		}
		if (!wantsSubscription && !policy.byokAllowed) {
			throw ApiError.forbidden(
				"Tenant Adminが個人API Key（BYOK）を許可していません。",
				ReasonCodes.PROVIDER_POLICY_DENIED,
			);
		}
		if (!policy.allowedProviderIds.includes(input.providerId)) {
			throw ApiError.forbidden(
				"このProviderはProvider Policyが未確認のため個人認証を登録できません。",
				ReasonCodes.PROVIDER_POLICY_UNVERIFIED,
			);
		}

		const created: PersonalCredential = {
			id: this.store.nextId("cred"),
			userId: this.authed?.user.id ?? "user-01",
			providerId: input.providerId,
			providerLabel:
				seed.providers.find((p) => p.id === input.providerId)?.displayName ?? input.providerId,
			displayName: input.displayName,
			authMethod: input.authMethod,
			secretRef: input.secretRef,
			// 参照の末尾だけを手掛かりとして残す。本文は保持しない。
			maskedHint: `••••${input.secretRef.slice(-4)}`,
			status: "ACTIVE",
			lastVerifiedAt: null,
			lastTestResult: null,
			lastTestReason: null,
			createdAt: this.iso(),
		};
		this.store.mutate((state) => state.personalCredentials.push(created));
		this.audit("credential.create", "PERSONAL_CREDENTIAL", created.id, "個人認証を登録");
		return created;
	}

	async testPersonalCredential(scope: Scope, credentialId: string) {
		this.assertScope(scope);
		await this.delay();
		const credential = this.store.read().personalCredentials.find((c) => c.id === credentialId);
		if (!credential) throw ApiError.notFound("認証設定が見つかりません。");
		// 期限切れ状態のものは失敗させ、原因別の復旧導線を出せるようにする。
		const failing = credential.status === "REAUTH_REQUIRED" || credential.status === "REVOKED";
		const verifiedAt = this.iso();
		this.store.mutate((state) => {
			const target = state.personalCredentials.find((c) => c.id === credentialId);
			if (!target) return;
			target.lastVerifiedAt = verifiedAt;
			target.lastTestResult = failing ? "FAILED" : "SUCCESS";
			target.lastTestReason = failing ? ReasonCodes.AUTH_PROFILE_EXPIRED : null;
			if (!failing) target.status = "ACTIVE";
		});
		this.audit("credential.test", "PERSONAL_CREDENTIAL", credentialId, "接続Testを実行");
		return {
			result: failing ? ("FAILED" as const) : ("SUCCESS" as const),
			reason: failing ? ReasonCodes.AUTH_PROFILE_EXPIRED : null,
			verifiedAt,
		};
	}

	async revokePersonalCredential(scope: Scope, credentialId: string): Promise<void> {
		this.assertScope(scope);
		await this.delay();
		this.store.mutate((state) => {
			state.personalCredentials = state.personalCredentials.filter((c) => c.id !== credentialId);
		});
		this.audit("credential.revoke", "PERSONAL_CREDENTIAL", credentialId, "個人認証を削除");
	}

	async getSessionAuthRoutes(scope: Scope, sessionId: string): Promise<AuthRouteView[]> {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const session = state.sessions.find((s) => s.id === sessionId && s.tenantId === scope.tenantId);
		if (!session) throw ApiError.notFound("Session not found");
		const userId = this.authed?.user.id ?? session.ownerUserId;
		const creds = state.personalCredentials.filter((c) => c.userId === userId);
		const anthropicSub = creds.find(
			(c) => c.providerId === "provider-anthropic" && c.authMethod === "SUBSCRIPTION_LOGIN",
		);
		const openaiApi = creds.find(
			(c) => c.providerId === "provider-openai" && c.authMethod === "API_KEY",
		);
		const codingAllowed = state.poolAssignments.some(
			(a) => a.poolId === "pool-claude-coding" && a.subjectId === userId && a.effect === "ALLOW",
		);

		return [
			{
				routeKey: "claude-coding",
				providerLabel: "Anthropic",
				taskTypes: ["CODING", "DESIGN", "DOCUMENT"],
				candidates: [
					{
						label: anthropicSub?.displayName ?? "個人Claude Subscription（未登録）",
						credentialClass: "USER_SUBSCRIPTION",
						poolAlias: null,
						state: anthropicSub?.status === "ACTIVE" ? "PRIMARY" : "UNAVAILABLE",
						unavailableReason: anthropicSub
							? anthropicSub.status === "ACTIVE"
								? null
								: "再認証が必要です。"
							: "個人認証が登録されていません。",
					},
					{
						// System認証はPool Aliasのみ。物理Account IDは表示しない。
						label: "Claude Coding Standard",
						credentialClass: "SYSTEM_SUBSCRIPTION",
						poolAlias: "Claude Coding Standard",
						state: codingAllowed ? "FALLBACK" : "UNAVAILABLE",
						unavailableReason: codingAllowed
							? null
							: "Tenant Adminからこの用途の利用許可がありません。",
					},
				],
			},
			{
				routeKey: "openai-research",
				providerLabel: "OpenAI",
				taskTypes: ["RESEARCH", "PLANNING", "CODE_REVIEW"],
				candidates: [
					{
						label: openaiApi?.displayName ?? "個人OpenAI API Key（未登録）",
						credentialClass: "USER_API",
						poolAlias: null,
						state: openaiApi?.status === "ACTIVE" ? "PRIMARY" : "UNAVAILABLE",
						unavailableReason:
							openaiApi && openaiApi.status !== "ACTIVE"
								? "接続Testが失敗しています。再登録してください。"
								: openaiApi
									? null
									: "個人認証が登録されていません。",
					},
					{
						label: "Standard Assist",
						credentialClass: "SYSTEM_API",
						poolAlias: "Standard Assist",
						state: "FALLBACK",
						unavailableReason: null,
					},
				],
			},
		];
	}

	// -- project / session ---------------------------------------------------

	async listOrganizationProjects(
		organizationId: string,
		options?: { includeArchived?: boolean },
	): Promise<ProjectListItemView[]> {
		await this.delay();
		const projects = this.store
			.read()
			.projects.filter(
				(project) =>
					project.tenantId === organizationId &&
					(options?.includeArchived || project.status === "ACTIVE"),
			)
			.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
		const active = projects.filter((project) => project.status === "ACTIVE");
		return projects.map((project) => ({
			id: project.id,
			name: project.name,
			description: project.description,
			status: project.status,
			isDefault: project.id === active[0]?.id,
			workspaceType: project.workspaceType,
			verticalKey: project.verticalKey,
			defaultUcsId: project.defaultUcsId,
			createdAt: project.createdAt,
		}));
	}

	async listWorkspaceTypes(organizationId: string): Promise<WorkspaceTypeList> {
		await this.delay();
		if (!this.authed || this.authed.tenant.id !== organizationId) {
			throw ApiError.notFound("Organization not found");
		}
		if (seed.workspaceTypeDefinitions.length === 0) {
			throw ApiError.validation(
				"ワークスペースタイプの Vertical が未接続です（Mock seed 欠落）。",
			);
		}
		return {
			items: structuredClone(seed.workspaceTypeDefinitions),
		};
	}

	async createProject(organizationId: string, input: CreateProjectRequest): Promise<Project> {
		await this.delay();
		if (!this.authed || this.authed.tenant.id !== organizationId) {
			throw ApiError.notFound("Organization not found");
		}
		if (
			!["tenant_member", "tenant_admin", "platform_admin"].includes(this.authed.membership.role)
		) {
			throw ApiError.forbidden("ワークスペースを作成する権限がありません");
		}
		const workspaceType = input.workspaceType ?? "generic";
		const template = seed.workspaceTypeDefinitions.find((item) => item.type === workspaceType);
		if (!template) {
			throw ApiError.validation(
				`ワークスペースタイプ ${workspaceType} の Vertical が見つかりません。`,
			);
		}
		const project: Project = {
			id: this.store.nextId("project"),
			tenantId: organizationId,
			name: input.name.trim(),
			description: input.description ?? "",
			ownerUserId: this.authed.user.id,
			status: "ACTIVE",
			workspaceType: template.type,
			verticalKey: template.verticalKey,
			defaultUcsId: template.defaultUcsId,
			createdAt: new Date().toISOString(),
		};
		this.store.mutate((state) => state.projects.push(project));
		return structuredClone(project);
	}

	async archiveProject(organizationId: string, projectId: string): Promise<Project> {
		this.assertWorkspaceMember(organizationId, projectId);
		return this.setProjectStatus(organizationId, projectId, "ARCHIVED");
	}

	async restoreProject(organizationId: string, projectId: string): Promise<Project> {
		this.assertWorkspaceMember(organizationId, projectId);
		return this.setProjectStatus(organizationId, projectId, "ACTIVE");
	}

	async listOrganizationRepositories(organizationId: string): Promise<GitRepository[]> {
		await this.delay();
		if (this.authed?.tenant.id !== organizationId)
			throw ApiError.notFound("Organization not found");
		return structuredClone(this.store.read().repositories);
	}

	async listProjectGitRepositories(
		organizationId: string,
		projectId: string,
	): Promise<GitRepository[]> {
		await this.delay();
		this.assertProjectTenant(organizationId, projectId);
		const state = this.store.read();
		const mappedIds = new Set(
			state.projectRepositoryConnections
				.filter((item) => item.projectId === projectId)
				.map((item) => item.repositoryId),
		);
		return structuredClone(state.repositories.filter((repo) => mappedIds.has(repo.id)));
	}

	async ensureProjectGitRepository(
		organizationId: string,
		projectId: string,
		input: Partial<EnsureGitRepositoryRequest> = {},
	): Promise<EnsureGitRepositoryResponse> {
		await this.delay();
		this.assertProjectTenant(organizationId, projectId);
		const existing = await this.listProjectGitRepositories(organizationId, projectId);
		if (existing[0]) {
			return { ...structuredClone(existing[0]), created: false };
		}
		const name =
			input.name ??
			`ws-${projectId.replace(/[^a-zA-Z0-9._-]/g, "-").replace(/^-+|-+$/g, "") || "project"}`.slice(
				0,
				100,
			);
		const found = this.store.read().repositories.find((repo) => repo.name === name);
		let repository = found;
		let created = false;
		if (!repository) {
			repository = {
				id: this.store.nextId("repository"),
				name,
				cloneUrl: `https://git.example.test/zerovia/${name}.git`,
				defaultBranch: input.defaultBranch ?? "main",
				mirrorStatus: "SYNCED",
			};
			created = true;
			this.store.mutate((state) => {
				state.repositories.push(repository!);
			});
		}
		const connection: ProjectRepositoryConnection = {
			projectId,
			repositoryId: repository.id,
			repositoryName: repository.name,
			provider: "ZEROVIA",
			connectedAt: new Date().toISOString(),
		};
		this.store.mutate((state) => {
			if (
				!state.projectRepositoryConnections.some(
					(item) => item.projectId === projectId && item.repositoryId === repository!.id,
				)
			) {
				state.projectRepositoryConnections.push(connection);
			}
		});
		return { ...structuredClone(repository), created };
	}

	async listProjectRepositories(
		organizationId: string,
		projectId: string,
	): Promise<ProjectRepositoryConnection[]> {
		await this.delay();
		this.assertProjectTenant(organizationId, projectId);
		return structuredClone(
			this.store.read().projectRepositoryConnections.filter((item) => item.projectId === projectId),
		);
	}

	async connectProjectRepository(
		organizationId: string,
		projectId: string,
		repositoryId: string,
	): Promise<ProjectRepositoryConnection> {
		await this.delay();
		this.assertTenantAdmin({ tenantId: organizationId, projectId });
		this.assertProjectTenant(organizationId, projectId);
		const repository = this.store.read().repositories.find((item) => item.id === repositoryId);
		if (!repository) throw ApiError.notFound("Repository not found");
		const existing = this.store
			.read()
			.projectRepositoryConnections.find(
				(item) => item.projectId === projectId && item.repositoryId === repositoryId,
			);
		if (existing) return structuredClone(existing);
		const connection: ProjectRepositoryConnection = {
			projectId,
			repositoryId,
			repositoryName: repository.name,
			provider: repository.name === "Zerovia" ? "ZEROVIA" : "AGENTRY",
			connectedAt: new Date().toISOString(),
		};
		this.store.mutate((state) => state.projectRepositoryConnections.push(connection));
		return structuredClone(connection);
	}

	async disconnectProjectRepository(
		organizationId: string,
		projectId: string,
		repositoryId: string,
	): Promise<void> {
		await this.delay();
		this.assertTenantAdmin({ tenantId: organizationId, projectId });
		this.assertProjectTenant(organizationId, projectId);
		this.store.mutate((state) => {
			state.projectRepositoryConnections = state.projectRepositoryConnections.filter(
				(item) => item.projectId !== projectId || item.repositoryId !== repositoryId,
			);
		});
	}

	private async setProjectStatus(
		organizationId: string,
		projectId: string,
		status: Project["status"],
	): Promise<Project> {
		await this.delay();
		const project = this.assertProjectTenant(organizationId, projectId);
		project.status = status;
		this.store.mutate(() => undefined);
		return structuredClone(project);
	}

	private assertProjectTenant(organizationId: string, projectId: string): Project {
		if (this.authed?.tenant.id !== organizationId)
			throw ApiError.notFound("Organization not found");
		const project = this.store
			.read()
			.projects.find((item) => item.id === projectId && item.tenantId === organizationId);
		if (!project) throw ApiError.notFound("Project not found");
		return project;
	}

	async listProjects(scope: Scope) {
		this.assertScope(scope);
		await this.delay();
		return this.store
			.read()
			.projects.filter((p) => p.tenantId === scope.tenantId)
			.map((p) => ({ id: p.id, name: p.name, description: p.description }));
	}

	async listSessions(scope: Scope, query?: ListQuery): Promise<Page<AgentSession>> {
		this.assertScope(scope);
		await this.delay();
		const items = this.store
			.read()
			.sessions.filter((s) => s.tenantId === scope.tenantId)
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
		return this.page(snapshot(items), query);
	}

	async createSession(scope: Scope, input: CreateAgentSessionRequest): Promise<AgentSession> {
		this.assertScope(scope);
		await this.delay();
		const entitlement = this.store.read().catalogEntitlements.find((e) => e.ucsId === input.ucsId);
		if (!entitlement?.enabled) {
			throw ApiError.forbidden(
				entitlement?.restrictedReason ?? "このUCSはTenantで利用が許可されていません。",
				ReasonCodes.ASSIGNMENT_DENIED,
			);
		}
		const ucs = seed.ucsList.find((u) => u.id === input.ucsId);
		if (!ucs) throw ApiError.notFound("UCS not found");
		const owner = this.authed?.user.id ?? "user-01";
		const created: AgentSession = {
			id: this.store.nextId("session"),
			tenantId: scope.tenantId,
			projectId: input.projectId,
			ownerUserId: owner,
			ucsId: input.ucsId,
			agentId: ucs.agentId,
			businessPurpose: input.businessPurpose,
			sessionMode: input.sessionMode,
			title: input.title,
			status: "ACTIVE",
			securityClassification: input.securityClassification,
			createdBy: owner,
			createdAt: this.iso(),
			updatedAt: this.iso(),
		};
		const opening = this.openingMessage(created);
		this.store.mutate((state) => {
			state.sessions.unshift(created);
			state.messages.push(opening);
		});
		this.audit("session.create", "SESSION", created.id, `UCS ${input.ucsId} でSessionを作成`);
		return created;
	}

	private openingMessage(session: AgentSession): ChatMessage {
		const pack = verticalPackageFor(session.ucsId);
		const first = pack?.guidedQuestions[0];
		return {
			id: this.store.nextId("msg"),
			sessionId: session.id,
			tenantId: session.tenantId,
			sequence: 0,
			role: "AGENT",
			content: first
				? `${agentNameOf(session.agentId)}が担当します。まず ${first.prompt}`
				: `${agentNameOf(session.agentId)}が担当します。ご依頼内容を教えてください。`,
			providerInvocationId: null,
			routingDecisionId: null,
			executionId: null,
			createdAt: this.iso(),
		};
	}

	async getSession(scope: Scope, sessionId: string): Promise<SessionDetail> {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const session = state.sessions.find((s) => s.id === sessionId && s.tenantId === scope.tenantId);
		if (!session) throw ApiError.notFound("Session not found");
		return snapshot({
			session,
			messages: state.messages
				.filter((m) => m.sessionId === session.id)
				.sort((a, b) => a.sequence - b.sequence),
			executions: state.executions.filter((e) => e.sessionId === session.id),
			artifacts: state.artifacts.filter((a) => a.generatedInSessionId === session.id),
		});
	}

	async sendChat(
		scope: Scope,
		sessionId: string,
		input: SendChatMessageRequest,
		idempotencyKey: string,
	) {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const session = state.sessions.find((s) => s.id === sessionId && s.tenantId === scope.tenantId);
		if (!session) throw ApiError.notFound("Session not found");

		const bodyHash = stableHash({ sessionId, ...input });
		const known = state.idempotency[idempotencyKey];
		if (known) {
			// 同じKey・同じBodyはReplay。同じKey・違うBodyはMismatch（Handoff条件）。
			if (known.bodyHash !== bodyHash) {
				throw ApiError.conflict(
					"同じIdempotency-Keyで異なる内容が送信されました。",
					ReasonCodes.IDEMPOTENCY_KEY_MISMATCH,
				);
			}
			const [userId, agentId] = known.resultRef.split("|");
			const userMessage = state.messages.find((m) => m.id === userId);
			const agentMessage = state.messages.find((m) => m.id === agentId);
			if (userMessage && agentMessage) {
				return {
					userMessage,
					agentMessage,
					executionId: agentMessage.executionId,
					replayed: true,
				};
			}
		}

		const existing = state.messages.filter((m) => m.sessionId === session.id);
		const nextSeq = existing.length;
		const userMessage: ChatMessage = {
			id: this.store.nextId("msg"),
			sessionId: session.id,
			tenantId: session.tenantId,
			sequence: nextSeq,
			role: "USER",
			content: input.content,
			providerInvocationId: null,
			routingDecisionId: null,
			executionId: null,
			createdAt: this.iso(),
		};

		const answeredCount = existing.filter((m) => m.role === "USER").length + 1;
		const pack = verticalPackageFor(session.ucsId);
		const questions = pack?.guidedQuestions ?? [];
		const nextQuestion = questions[answeredCount];
		const guidedComplete =
			session.sessionMode === "GUIDED_WORKFLOW" && questions.length > 0 && !nextQuestion;

		let executionId: string | null = null;
		if (input.startWorkflow || guidedComplete) {
			executionId = this.startExecutionForSession(session);
		}

		const agentMessage: ChatMessage = {
			id: this.store.nextId("msg"),
			sessionId: session.id,
			tenantId: session.tenantId,
			sequence: nextSeq + 1,
			role: "AGENT",
			content: nextQuestion
				? `承知しました。次に ${nextQuestion.prompt}${nextQuestion.helpText ? `\n\n${nextQuestion.helpText}` : ""}`
				: guidedComplete
					? "必要な情報が揃いました。Workflowを開始します。進捗はProgressで確認でき、承認が必要な段階でお知らせします。"
					: "承知しました。内容を確認して作業を進めます。",
			providerInvocationId: `pinv-${stableHash(userMessage.id)}`,
			routingDecisionId: `rdec-${stableHash(userMessage.id)}`,
			executionId,
			createdAt: this.iso(1000),
		};

		this.store.mutate((s) => {
			s.messages.push(userMessage, agentMessage);
			s.idempotency[idempotencyKey] = {
				bodyHash,
				resultRef: `${userMessage.id}|${agentMessage.id}`,
			};
			const target = s.sessions.find((x) => x.id === session.id);
			if (target) target.updatedAt = agentMessage.createdAt;
			s.costEvents.unshift({
				id: `cost-${s.sequence++}`,
				sessionId: session.id,
				executionId: executionId ?? "",
				providerId: "provider-fake",
				modelId: "model-fake",
				meteringMode: "ACTUAL_USAGE",
				inputTokens: input.content.length * 2,
				outputTokens: agentMessage.content.length * 2,
				amount: Math.round(input.content.length / 4),
				currency: "JPY",
				createdAt: agentMessage.createdAt,
			});
		});

		return { userMessage, agentMessage, executionId, replayed: false };
	}

	async submitUserInput(
		scope: Scope,
		executionId: string,
		_input: import("../types.js").UserInputSubmission,
		_idempotencyKey: string,
	): Promise<void> {
		this.assertScope(scope);
		await this.delay();
		const execution = this.store
			.read()
			.executions.find((item) => item.id === executionId && item.tenantId === scope.tenantId);
		if (!execution) throw ApiError.notFound("Execution not found");
	}

	// -- work order board -----------------------------------------------------

	async getWorkOrderBoard(scope: Scope) {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		return snapshot({
			orders: state.workOrders
				.filter((item) => item.tenantId === scope.tenantId && item.projectId === scope.projectId)
				.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)),
			progress: state.workOrderProgress,
			designSessionId: "project-design-session-01",
			designFrames: state.workOrderFrames
				.filter((frame) => frame.orderId === null)
				.sort((left, right) => left.sequence - right.sequence),
		});
	}

	async getWorkOrderSessionFrames(
		scope: Scope,
		orderId: string,
		sessionId: string,
	): Promise<WorkOrderFrameView[]> {
		const detail = await this.getWorkOrderDetail(scope, orderId);
		return detail.frames.filter((frame) => frame.sessionId === sessionId);
	}

	async getWorkOrderDetail(scope: Scope, orderId: string) {
		this.assertScope(scope);
		await this.delay();
		return this.workOrderDetail(scope, orderId);
	}

	async transitionWorkOrder(
		scope: Scope,
		orderId: string,
		to: Exclude<WorkOrderStatus, "blocked">,
		reason?: string,
	): Promise<WorkOrder> {
		this.assertScope(scope);
		await this.delay();
		const now = this.iso();
		const actorId = this.authed?.user.id ?? "user-member-01";
		const updated = this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) => item.id === orderId && item.projectId === scope.projectId && !item.deletedAt,
			);
			if (!order) throw ApiError.notFound("Work order not found");
			order.status = to;
			order.blockedFromStatus = null;
			order.attentionRequired = to === "verified";
			order.updatedAt = now;
			state.workOrderRecords.push({
				id: `record-${state.sequence++}`,
				orderId,
				seq: state.workOrderRecords.filter((item) => item.orderId === orderId).length + 1,
				kind: to === "accepted" ? "受け入れ" : "備考",
				bodyMarkdown: reason || `${to} へ更新しました。`,
				actorType: "human",
				actorId,
				createdAt: now,
			});
			if (to === "issued") {
				state.workOrderProgress[orderId] = {
					sequence: 0,
					phase: "queued",
					label: "実装準備",
					startedAt: now,
					retryCount: 0,
					retryReason: null,
					nextRetryAt: null,
					live: true,
					llmActiveSeconds: 0,
					failureClass: null,
					warning: null,
					wallClock: emptyWallClock(),
					journey: null,
				};
			}
			return order;
		});
		return snapshot(updated);
	}

	async deleteWorkOrder(scope: Scope, orderId: string): Promise<WorkOrder> {
		this.assertScope(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) =>
					item.id === orderId &&
					item.tenantId === scope.tenantId &&
					item.projectId === scope.projectId &&
					!item.deletedAt,
			);
			if (!order) throw ApiError.notFound("Work order not found");
			if (state.workOrderProgress[orderId]?.live) {
				throw ApiError.conflict("Work order has a live execution", "WORK_ORDER_EXECUTION_LIVE");
			}
			const now = this.iso();
			order.deletedAt = now;
			order.deletedBy = this.authed?.user.id ?? null;
			order.updatedAt = now;
			return order;
		});
		return snapshot(updated);
	}

	private mockProjectSettings = new Map<string, WorkOrderProjectSettings>();

	async getWorkOrderProjectSettings(scope: Scope): Promise<WorkOrderProjectSettings> {
		this.assertScope(scope);
		await this.delay();
		return (
			this.mockProjectSettings.get(scope.projectId) ?? {
				runtimeParallelism: 1,
				updatedBy: null,
				updatedAt: null,
			}
		);
	}

	async updateWorkOrderProjectSettings(
		scope: Scope,
		input: { runtimeParallelism: number; reason?: string },
	): Promise<WorkOrderProjectSettings> {
		this.assertScope(scope);
		await this.delay();
		const next: WorkOrderProjectSettings = {
			runtimeParallelism: Math.min(4, Math.max(1, Math.trunc(input.runtimeParallelism))),
			updatedBy: this.authed?.user.id ?? "user-member-01",
			updatedAt: this.iso(),
		};
		this.mockProjectSettings.set(scope.projectId, next);
		return next;
	}

	async retryWorkOrder(
		scope: Scope,
		orderId: string,
		reason: string,
	): Promise<{ order: WorkOrder; jobId: string }> {
		this.assertScope(scope);
		await this.delay();
		const now = this.iso();
		const updated = this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) => item.id === orderId && item.projectId === scope.projectId && !item.deletedAt,
			);
			if (!order) throw ApiError.notFound("Work order not found");
			if (!order.attentionRequired)
				throw ApiError.conflict(
					"Work Order has no failed automation to resume",
					"WORK_ORDER_RESUME_INVALID",
				);
			order.attentionRequired = false;
			order.updatedAt = now;
			const progress = state.workOrderProgress[orderId];
			if (progress) {
				progress.live = true;
				progress.retryCount += 1;
				progress.retryReason = null;
				progress.failureClass = null;
			}
			state.workOrderRecords.push({
				id: `record-${state.sequence++}`,
				orderId,
				seq: state.workOrderRecords.filter((item) => item.orderId === orderId).length + 1,
				kind: "備考",
				bodyMarkdown: `resume: ${reason}`,
				actorType: "human",
				actorId: this.authed?.user.id ?? "user-member-01",
				createdAt: now,
			});
			return order;
		});
		return { order: snapshot(updated), jobId: `job-${Date.now().toString(36)}` };
	}

	async restoreWorkOrder(scope: Scope, orderId: string): Promise<WorkOrder> {
		this.assertScope(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) => item.id === orderId && item.projectId === scope.projectId,
			);
			if (!order) throw ApiError.notFound("Work order not found");
			order.deletedAt = null;
			order.deletedBy = null;
			order.updatedAt = this.iso();
			return order;
		});
		return snapshot(updated);
	}

	async answerWorkOrderQuestion(
		scope: Scope,
		orderId: string,
		questionId: string,
		answerMarkdown: string,
	) {
		this.assertScope(scope);
		await this.delay();
		if (!answerMarkdown.trim()) throw ApiError.validation("回答を入力してください。");
		const now = this.iso();
		this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) => item.id === orderId && item.projectId === scope.projectId,
			);
			const question = state.workOrderQuestions.find(
				(item) => item.id === questionId && item.orderId === orderId,
			);
			if (!order || !question) throw ApiError.notFound("Question not found");
			question.status = "answered";
			question.answerMarkdown = answerMarkdown.trim();
			question.answeredByActorType = "human";
			question.answeredByActorId = this.authed?.user.id ?? "user-member-01";
			question.answeredAt = now;
			order.status = order.blockedFromStatus ?? "implemented";
			order.blockedFromStatus = null;
			order.attentionRequired = false;
			order.updatedAt = now;
			state.workOrderProgress[orderId] = {
				sequence: 0,
				phase: "resume",
				label: "裁定を反映して再開",
				startedAt: now,
				retryCount: 0,
				retryReason: null,
				nextRetryAt: null,
				live: true,
				llmActiveSeconds: 0,
				failureClass: null,
				warning: null,
				wallClock: emptyWallClock(),
				journey: null,
			};
		});
		return this.workOrderDetail(scope, orderId);
	}

	async sendBoardDesignMessage(scope: Scope, body: string) {
		this.assertScope(scope);
		await this.delay();
		const content = body.trim();
		if (!content) throw ApiError.validation("依頼内容を入力してください。");
		const now = this.iso();
		const userId = this.store.nextId("frame");
		const assistantId = this.store.nextId("frame");
		const orderId = this.store.nextId("wo");
		const title = content.split("\n")[0]?.slice(0, 80) || "新しい依頼";
		this.store.mutate((state) => {
			const sequence = state.workOrderFrames.filter((frame) => frame.orderId === null).length + 1;
			state.workOrderFrames.push(
				{
					id: userId,
					orderId: null,
					sessionId: "project-design-session-01",
					sequence,
					role: "user",
					body: content,
					createdAt: now,
				},
				{
					id: assistantId,
					orderId: null,
					sessionId: "project-design-session-01",
					sequence: sequence + 1,
					role: "assistant",
					body: `「${title}」を下書きとして起票しました。内容を確認して発注できます。`,
					createdAt: now,
				},
			);
			state.workOrders.push({
				id: orderId,
				tenantId: scope.tenantId,
				projectId: scope.projectId,
				title,
				kind: "implementation",
				deployStage: null,
				targetRepo: "agentry",
				branch: `cs/mock-${state.sequence}`,
				specMarkdown: `# ${title}\n\n## 依頼内容\n\n${content}`,
				journeyScript: null,
				scheduledAt: null,
				journeyLlmCapSeconds: null,
				journeyFault: null,
				campaignId: null,
				campaignOrdinal: null,
				campaignSize: null,
				status: "draft",
				blockedFromStatus: null,
				createdBy: this.authed?.user.id ?? "user-member-01",
				attentionRequired: true,
				deletedAt: null,
				deletedBy: null,
				requiredSecrets: [],
				createdAt: now,
				updatedAt: now,
			});
		});
		const frames = this.store
			.read()
			.workOrderFrames.filter((frame) => frame.orderId === null)
			.sort((left, right) => left.sequence - right.sequence);
		return { frames: snapshot(frames), createdOrderIds: [orderId] };
	}

	async sendWorkOrderSessionMessage(
		scope: Scope,
		orderId: string,
		role: WorkOrderSessionRole,
		body: string,
	) {
		this.assertScope(scope);
		await this.delay();
		const content = body.trim();
		if (!content) throw ApiError.validation("メッセージを入力してください。");
		const now = this.iso();
		this.store.mutate((state) => {
			const order = state.workOrders.find(
				(item) => item.id === orderId && item.projectId === scope.projectId,
			);
			if (!order) throw ApiError.notFound("Work order not found");
			let session = state.workOrderSessions.find(
				(item) => item.orderId === orderId && item.role === role,
			);
			if (!session) {
				session = {
					id: `order-session-${state.sequence++}`,
					orderId,
					role,
					attempt: 1,
					agentSessionId: `agent-session-${state.sequence++}`,
					runtimeId: `runtime-${state.sequence++}`,
					status: "active",
					framesAvailable: true,
					createdAt: now,
					endedAt: null,
				};
				state.workOrderSessions.push(session);
			}
			const sequence =
				state.workOrderFrames.filter((frame) => frame.orderId === orderId).length + 1;
			state.workOrderFrames.push(
				{
					id: `frame-${state.sequence++}`,
					orderId,
					sessionId: session.id,
					sequence,
					role: "user",
					body: content,
					createdAt: now,
				},
				{
					id: `frame-${state.sequence++}`,
					orderId,
					sessionId: session.id,
					sequence: sequence + 1,
					role: "assistant",
					body: `${roleLabel(role)}担当に共有しました。Mockでは履歴への反映までを確認できます。`,
					createdAt: now,
				},
			);
		});
		return snapshot(
			this.store
				.read()
				.workOrderFrames.filter((frame) => frame.orderId === orderId)
				.sort((left, right) => left.sequence - right.sequence),
		);
	}

	subscribeDesignFrames(
		_scope: Scope,
		_designSessionId: string,
		_options: WorkOrderStreamOptions<ConversationFrame>,
	): ProgressSubscription {
		return { close() {} };
	}

	subscribeWorkOrderFrames(
		_scope: Scope,
		_orderId: string,
		_sessionId: string,
		_options: WorkOrderStreamOptions<ConversationFrame>,
	): ProgressSubscription {
		return { close() {} };
	}

	subscribeWorkOrderProgress(
		_scope: Scope,
		_orderId: string,
		_options: WorkOrderStreamOptions<WorkOrderProgress>,
	): ProgressSubscription {
		return { close() {} };
	}

	private workOrderDetail(scope: Scope, orderId: string) {
		const state = this.store.read();
		const order = state.workOrders.find(
			(item) =>
				item.id === orderId &&
				item.tenantId === scope.tenantId &&
				item.projectId === scope.projectId,
		);
		if (!order) throw ApiError.notFound("Work order not found");
		return snapshot({
			order,
			questions: state.workOrderQuestions.filter((item) => item.orderId === orderId),
			records: state.workOrderRecords.filter((item) => item.orderId === orderId),
			evidence: state.workOrderEvidence.filter((item) => item.orderId === orderId),
			sessions: state.workOrderSessions.filter((item) => item.orderId === orderId),
			frames: state.workOrderFrames
				.filter((item) => item.orderId === orderId)
				.sort((left, right) => left.sequence - right.sequence),
			progress: state.workOrderProgress[orderId] ?? null,
		});
	}

	private startExecutionForSession(session: AgentSession, input?: StartExecutionRequest): string {
		const definition =
			seed.workflowDefinitions.find(
				(d) => d.key === seed.ucsList.find((u) => u.id === session.ucsId)?.workflowDefinitionKey,
			) ?? seed.workflowDefinitions[0];
		if (!definition) throw ApiError.notFound("Workflow definition not found");
		const id = this.store.nextId("execution");
		const execution: WorkflowExecution = {
			id,
			tenantId: session.tenantId,
			projectId: session.projectId,
			sessionId: session.id,
			ucsId: session.ucsId,
			workflowDefinitionId: definition.id,
			workflowVersion: definition.version,
			snapshotHash: `sha256:${stableHash([definition.id, session.id])}`,
			effectiveWorkflowHash: input?.effectiveWorkflowHash ?? null,
			effectiveWorkflowProvenance: input?.effectiveWorkflowProvenance ?? null,
			rootExecutionId: id,
			parentExecutionId: null,
			resumeFromCheckpointId: null,
			replayOfExecutionId: null,
			status: "RUNNING",
			waitReason: null,
			failureClass: null,
			failureDetail: null,
			triggerType: "USER",
			idempotencyKey: `start-${id}`,
			stateVersion: 1,
			startedByUserId: session.createdBy,
			ownerUserId: session.ownerUserId,
			startedAt: this.iso(),
			deadlineAt: null,
			currentCheckpointId: null,
			estimatedCost: 0,
			committedCost: 0,
			currency: "JPY",
			createdAt: this.iso(),
			updatedAt: this.iso(),
			endedAt: null,
		};
		this.store.mutate((state) => state.executions.unshift(execution));
		this.audit(
			"execution.start",
			"WORKFLOW_EXECUTION",
			id,
			"Guided Interview完了によりWorkflow開始",
		);
		return id;
	}

	// -- workflow -------------------------------------------------------------
	async startExecution(scope: Scope, input: StartExecutionRequest): Promise<WorkflowExecution> {
		this.assertScope(scope);
		await this.delay();
		const body = StartExecutionRequestSchema.parse(input);
		const session = this.store
			.read()
			.sessions.find(
				(item) =>
					item.id === body.sessionId &&
					item.tenantId === scope.tenantId &&
					item.projectId === scope.projectId,
			);
		if (!session) throw ApiError.notFound("Session not found");
		const id = this.startExecutionForSession(session, body);
		const execution = this.store.read().executions.find((item) => item.id === id);
		if (!execution) throw ApiError.notFound("Execution not found");
		return snapshot(execution);
	}

	async listExecutions(scope: Scope, query?: ListQuery): Promise<Page<WorkflowExecution>> {
		this.assertScope(scope);
		await this.delay();
		const items = this.store
			.read()
			.executions.filter((e) => e.tenantId === scope.tenantId)
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
		return this.page(snapshot(items), query);
	}

	async getTimeline(scope: Scope, executionId: string): Promise<ExecutionTimeline> {
		this.assertScope(scope);
		await this.delay();
		const execution = this.store
			.read()
			.executions.find((e) => e.id === executionId && e.tenantId === scope.tenantId);
		if (!execution) throw ApiError.notFound("Execution not found");
		return buildTimeline(snapshot(execution));
	}

	async sendWorkflowCommand(
		scope: Scope,
		executionId: string,
		command: WorkflowCommand,
		options: CommandOptions,
	): Promise<WorkflowExecution> {
		this.assertScope(scope);
		await this.delay();
		const current = this.store
			.read()
			.executions.find((e) => e.id === executionId && e.tenantId === scope.tenantId);
		if (!current) throw ApiError.notFound("Execution not found");

		if (current.stateVersion !== options.expectedStateVersion) {
			throw ApiError.conflict(
				"他の操作によって状態が更新されています。最新の状態を再取得してください。",
				ReasonCodes.STATE_VERSION_CONFLICT,
				{ current: current.stateVersion },
			);
		}
		if (isTerminal(current.status) && command !== "retry") {
			throw new ApiError({
				code: ErrorCodes.PRECONDITION_FAILED,
				message: "終端状態のExecutionは再開できません。Retryは新しいExecutionとして開始します。",
				reason: ReasonCodes.EXECUTION_TERMINAL,
			});
		}

		const next = this.store.mutate((state) => {
			const target = state.executions.find((e) => e.id === executionId);
			if (!target) throw ApiError.notFound("Execution not found");
			target.stateVersion += 1;
			target.updatedAt = this.iso();
			switch (command) {
				case "cancel":
					target.status = "CANCELLED";
					target.waitReason = null;
					target.endedAt = this.iso();
					break;
				case "pause":
					target.status = "SUSPENDED";
					target.waitReason = null;
					break;
				case "resume":
					target.status = "RUNNING";
					target.waitReason = null;
					break;
				case "retry":
					target.status = "RUNNING";
					target.waitReason = null;
					target.failureClass = null;
					target.endedAt = null;
					break;
			}
			return { ...target };
		});
		this.audit(`execution.${command}`, "WORKFLOW_EXECUTION", executionId, options.reason);
		return next;
	}

	subscribeProgress(
		scope: Scope,
		executionId: string,
		options: SubscribeOptions,
	): ProgressSubscription {
		const execution = this.store.read().executions.find((e) => e.id === executionId);
		if (!execution || execution.tenantId !== scope.tenantId) {
			queueMicrotask(() => options.onError(ApiError.notFound("Execution not found")));
			return { close() {} };
		}
		const frames = buildProgressEvents(execution);
		let index = frames.findIndex((f) => f.sequence > (options.lastSequence ?? -1));
		if (index < 0) index = frames.length;
		let expected = (options.lastSequence ?? -1) + 1;
		let stopped = false;

		const timer = setInterval(
			() => {
				if (stopped) return;
				const frame = frames[index];
				if (!frame) {
					clearInterval(timer);
					return;
				}
				index += 1;
				if (frame.sequence !== expected) {
					options.onGap(expected, frame.sequence);
				}
				expected = frame.sequence + 1;
				options.onEvent(frame);
			},
			Math.max(this.latency, 60),
		);

		return {
			close() {
				stopped = true;
				clearInterval(timer);
			},
		};
	}

	// -- approval --------------------------------------------------------------

	async listApprovals(scope: Scope): Promise<ApprovalRequest[]> {
		this.assertScope(scope);
		await this.delay();
		return snapshot(this.store.read().approvals.filter((a) => a.tenantId === scope.tenantId));
	}

	async getApproval(scope: Scope, approvalId: string): Promise<ApprovalRequest> {
		this.assertScope(scope);
		await this.delay();
		const found = this.store
			.read()
			.approvals.find((a) => a.id === approvalId && a.tenantId === scope.tenantId);
		if (!found) throw ApiError.notFound("Approval not found");
		return snapshot(found);
	}

	async decideApproval(
		scope: Scope,
		approvalId: string,
		input: {
			decision: ApprovalDecision;
			selectedArtifactId?: string;
			selectedArtifactVersion?: number;
			siteId: ApprovalRequest["siteId"];
			instructions: string;
			idempotencyKey: string;
		},
	): Promise<ApprovalRequest> {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const approval = state.approvals.find(
			(a) => a.id === approvalId && a.tenantId === scope.tenantId,
		);
		if (!approval) throw ApiError.notFound("Approval not found");
		if (approval.status !== "PENDING") {
			throw ApiError.conflict("この承認は既に処理されています。", ReasonCodes.WAIT_HANDLE_RESOLVED);
		}
		if (approval.indexArtifactId && input.decision === "APPROVED" && !input.selectedArtifactId) {
			throw ApiError.validation("承認する案を選んでください。");
		}
		if (input.decision === "CHANGES_REQUESTED" && !input.instructions.trim()) {
			throw ApiError.validation("修正指示を入力してください。");
		}

		const decidedAt = this.iso();
		const updated = this.store.mutate((s) => {
			const target = s.approvals.find((a) => a.id === approvalId);
			if (!target) throw ApiError.notFound("Approval not found");
			target.decision = input.decision;
			target.decidedBy = this.authed?.user.id ?? null;
			target.decidedAt = decidedAt;
			target.status = input.decision === "APPROVED" ? "PASSED" : "FAILED";

			const execution = s.executions.find((e) => e.id === target.executionId);
			if (execution) {
				execution.stateVersion += 1;
				execution.updatedAt = decidedAt;
				if (input.decision === "APPROVED") {
					execution.status = "SUCCEEDED";
					execution.waitReason = null;
					execution.endedAt = decidedAt;
				} else {
					// 差戻しはFAILEDではなく、修正Loopへ戻る RUNNING（design §20.7）。
					execution.status = "RUNNING";
					execution.waitReason = null;
				}
			}
			return { ...target };
		});
		this.audit(
			`approval.${input.decision.toLowerCase()}`,
			"APPROVAL",
			approvalId,
			input.instructions || "承認判断",
		);
		return updated;
	}

	// -- artifact / workspace / storage -----------------------------------------

	async listArtifacts(
		scope: Scope,
		query?: ListQuery & { sessionId?: string },
	): Promise<Page<Artifact>> {
		this.assertScope(scope);
		await this.delay();
		let items = this.store.read().artifacts.filter((a) => a.tenantId === scope.tenantId);
		if (query?.sessionId) {
			items = items.filter((a) => a.generatedInSessionId === query.sessionId);
		}
		// 同名Artifactは最新Versionのみを一覧に出す。旧VersionはVersion一覧から辿る。
		const latest = new Map<string, Artifact>();
		for (const a of items) {
			const key = `${a.generatedInSessionId}:${a.name}`;
			const prev = latest.get(key);
			if (!prev || a.version > prev.version) latest.set(key, a);
		}
		return this.page(
			snapshot([...latest.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt))),
			query,
		);
	}

	async createArtifact(scope: Scope, input: CreateArtifactRequest): Promise<Artifact> {
		this.assertScope(scope);
		await this.delay();
		if (input.projectId !== scope.projectId) {
			throw ApiError.notFound("Project not found");
		}
		const superseded = input.supersedesArtifactId
			? this.store
					.read()
					.artifacts.find(
						(artifact) =>
							artifact.id === input.supersedesArtifactId && artifact.tenantId === scope.tenantId,
					)
			: undefined;
		if (input.supersedesArtifactId && !superseded) {
			throw ApiError.notFound("Artifact not found", ReasonCodes.ARTIFACT_NOT_FOUND);
		}
		const id = this.store.nextId("artifact");
		const artifact: Artifact = {
			id,
			tenantId: scope.tenantId,
			projectId: scope.projectId,
			workspaceId: input.workspaceId ?? `workspace-${scope.projectId}`,
			ownerUserId: input.ownerUserId,
			generatedInSessionId: input.sessionId,
			generatedByExecutionId: input.executionId,
			generatedByNodeExecutionId: input.nodeExecutionId,
			generatedByActorType: input.generatedByActorType,
			generatedByActorId: input.generatedByActorId,
			authenticationProfileRef: input.authenticationProfileRef,
			name: input.name,
			type: input.type,
			mimeType: input.mimeType,
			version: (superseded?.version ?? 0) + 1,
			lineageId: superseded?.lineageId ?? id,
			storageRef: `mock/artifacts/${id}`,
			sizeBytes: input.content.length,
			checksum: checksumOf(input.content),
			checksumAlgorithm: "sha256",
			status: "COMMITTED",
			retentionUntil: null,
			createdAt: this.iso(),
		};
		this.store.mutate((state) => {
			if (superseded) superseded.status = "SUPERSEDED";
			state.artifacts.push(artifact);
			state.artifactContents[id] = input.content;
		});
		return snapshot(artifact);
	}

	async getArtifact(scope: Scope, artifactId: string): Promise<Artifact> {
		this.assertScope(scope);
		await this.delay();
		const found = this.store
			.read()
			.artifacts.find((a) => a.id === artifactId && a.tenantId === scope.tenantId);
		if (!found) throw ApiError.notFound("Artifact not found", ReasonCodes.ARTIFACT_NOT_FOUND);
		return snapshot(found);
	}

	async getArtifactContent(scope: Scope, artifactId: string) {
		const artifact = await this.getArtifact(scope, artifactId);
		const content = this.store.read().artifactContents[artifactId];
		if (content === undefined) {
			throw ApiError.notFound("Artifact content not found", ReasonCodes.ARTIFACT_NOT_FOUND);
		}
		// 取得のたびに本文からChecksumを再計算して照合する。
		const checksumVerified = checksumOf(content) === artifact.checksum;
		if (!checksumVerified) {
			throw new ApiError({
				code: ErrorCodes.UNPROCESSABLE,
				message: "成果物のChecksumが一致しません。内容を表示せず再取得してください。",
				reason: ReasonCodes.CHECKSUM_MISMATCH,
			});
		}
		return { artifact, content, checksumVerified };
	}

	async createDownloadTicket(scope: Scope, artifactId: string): Promise<DownloadTicket> {
		const artifact = await this.getArtifact(scope, artifactId);
		const expiresAt = this.iso(DOWNLOAD_TICKET_TTL_MS);
		this.audit("artifact.download", "ARTIFACT", artifactId, "Download URLを発行");
		return {
			url: `mock-download://${artifact.storageRef}?expires=${encodeURIComponent(expiresAt)}`,
			artifactId,
			expiresAt,
			checksum: artifact.checksum,
		};
	}

	async listArtifactVersions(scope: Scope, artifactId: string): Promise<Artifact[]> {
		const artifact = await this.getArtifact(scope, artifactId);
		return this.store
			.read()
			.artifacts.filter(
				(a) => a.generatedInSessionId === artifact.generatedInSessionId && a.name === artifact.name,
			)
			.sort((a, b) => b.version - a.version);
	}

	async transferArtifactOwnership(
		scope: Scope,
		artifactId: string,
		input: { toOwnerUserId: string; reason: string },
	): Promise<OwnershipTransferRecord> {
		const artifact = await this.getArtifact(scope, artifactId);
		const actor = this.authed?.user;
		const actorRole = this.authed?.membership.role;
		// Ownership Transferは所有者かTenant Adminのみ。ACL閲覧権だけでは移管できない。
		const permitted =
			actorRole === "tenant_admin" ||
			actorRole === "platform_admin" ||
			actor?.id === artifact.ownerUserId;
		if (!permitted) {
			throw ApiError.forbidden(
				"所有者移管の権限がありません。",
				ReasonCodes.OWNERSHIP_TRANSFER_NOT_PERMITTED,
			);
		}
		const record: OwnershipTransferRecord = {
			id: this.store.nextId("transfer"),
			resourceType: "ARTIFACT",
			resourceId: artifactId,
			fromOwnerUserId: artifact.ownerUserId,
			toOwnerUserId: input.toOwnerUserId,
			fromOwnerLabel: userLabel(artifact.ownerUserId),
			toOwnerLabel: userLabel(input.toOwnerUserId),
			reason: input.reason,
			requestedBy: actor?.id ?? "system",
			approvedBy: actor?.id ?? null,
			transferredAt: this.iso(),
		};
		this.store.mutate((state) => {
			const target = state.artifacts.find((a) => a.id === artifactId);
			if (target) target.ownerUserId = input.toOwnerUserId;
			state.ownershipTransfers.unshift(record);
		});
		this.audit("ownership.transfer", "ARTIFACT", artifactId, input.reason);
		return record;
	}

	async listOwnershipTransfers(scope: Scope, artifactId: string) {
		this.assertScope(scope);
		await this.delay();
		return this.store.read().ownershipTransfers.filter((t) => t.resourceId === artifactId);
	}

	async listWorkspaceEntries(scope: Scope): Promise<WorkspaceEntry[]> {
		this.assertScope(scope);
		await this.delay();
		return this.store
			.read()
			.workspaceEntries.filter(
				(w) => w.tenantId === scope.tenantId && w.projectId === scope.projectId,
			)
			.sort((a, b) => a.path.localeCompare(b.path));
	}

	async listStorageObjects(scope: Scope): Promise<StorageObject[]> {
		this.assertScope(scope);
		await this.delay();
		return seed.storageObjects.filter((s) => s.tenantId === scope.tenantId);
	}

	// -- cost / audit / usage / notification -------------------------------------

	async listCostEvents(scope: Scope, query?: ListQuery): Promise<Page<CostEventView>> {
		this.assertScope(scope);
		await this.delay();
		return this.page(this.store.read().costEvents, query);
	}

	async listAuditEvents(scope: Scope, query?: ListQuery): Promise<Page<AuditEventView>> {
		this.assertScope(scope);
		await this.delay();
		return this.page(this.store.read().auditEvents, query);
	}

	async getUsageSummary(scope: Scope): Promise<UsageSummary> {
		this.assertScope(scope);
		await this.delay();
		const state = this.store.read();
		const executions = state.executions.filter((e) => e.tenantId === scope.tenantId);
		const costs = state.costEvents;
		const modes = new Map<string, { executions: number; amount: number | null }>();
		for (const c of costs) {
			const prev = modes.get(c.meteringMode) ?? { executions: 0, amount: 0 };
			modes.set(c.meteringMode, {
				executions: prev.executions + 1,
				// 取得不能を0円と混同しない（design §13）。
				amount: c.amount === null ? null : (prev.amount ?? 0) + c.amount,
			});
		}
		return {
			period: "2026-08",
			executions: executions.length,
			succeededExecutions: executions.filter((e) => e.status === "SUCCEEDED").length,
			artifacts: state.artifacts.filter((a) => a.tenantId === scope.tenantId).length,
			inputTokens: costs.reduce((s, c) => s + c.inputTokens, 0),
			outputTokens: costs.reduce((s, c) => s + c.outputTokens, 0),
			amount: costs.reduce((s, c) => s + (c.amount ?? 0), 0),
			currency: "JPY",
			byMeteringMode: [...modes.entries()].map(([mode, v]) => ({ mode, ...v })),
		};
	}

	async listNotifications(scope: Scope): Promise<NotificationItem[]> {
		this.assertScope(scope);
		await this.delay();
		const userId = this.authed?.user.id;
		return this.store.read().notifications.filter((n) => n.userId === userId);
	}

	async markNotificationRead(scope: Scope, notificationId: string): Promise<void> {
		this.assertScope(scope);
		this.store.mutate((state) => {
			const target = state.notifications.find((n) => n.id === notificationId);
			if (target) target.read = true;
		});
	}

	// -- tenant admin ------------------------------------------------------------

	private assertTenantAdmin(scope: Scope): void {
		this.assertScope(scope);
		const role = this.authed?.membership.role;
		if (role !== "tenant_admin" && role !== "platform_admin") {
			throw ApiError.forbidden("Tenant Admin権限が必要です。");
		}
	}

	/** ワークスペースの論理削除／復元。作成と同じく tenant_member 以上。 */
	private assertWorkspaceMember(organizationId: string, projectId: string): void {
		this.assertProjectTenant(organizationId, projectId);
		const role = this.authed?.membership.role;
		if (
			role !== "tenant_member" &&
			role !== "tenant_admin" &&
			role !== "platform_admin"
		) {
			throw ApiError.forbidden("ワークスペースを削除する権限がありません");
		}
	}

	private assertSystemAdmin(): void {
		const role = this.authed?.membership.role;
		if (role !== "platform_admin" && role !== "platform_viewer") {
			throw ApiError.forbidden("Agentry System Admin権限が必要です。");
		}
	}

	async listMembers(scope: Scope): Promise<TenantMember[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return this.store.read().members.filter((m) => m.tenantId === scope.tenantId);
	}

	async updateMemberRole(scope: Scope, memberId: string, role: TenantMember["role"]) {
		this.assertTenantAdmin(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const target = state.members.find((m) => m.id === memberId && m.tenantId === scope.tenantId);
			if (!target) throw ApiError.notFound("Member not found");
			target.role = role;
			return { ...target };
		});
		this.audit("member.role_update", "MEMBER", memberId, `Roleを${role}へ変更`);
		return updated;
	}

	async listInvites(scope: Scope): Promise<TenantInvite[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return this.store.read().invites.filter((i) => i.tenantId === scope.tenantId);
	}

	async createInvite(scope: Scope, input: { email: string; role: TenantMember["role"] }) {
		this.assertTenantAdmin(scope);
		await this.delay();
		const id = this.store.nextId("invite");
		const invite: TenantInvite = {
			id,
			tenantId: scope.tenantId,
			email: input.email,
			role: input.role,
			status: "PENDING",
			invitedBy: this.authed?.user.id ?? "user-02",
			expiresAt: this.iso(7 * 24 * 3600_000),
			createdAt: this.iso(),
			token: `invite-token-${stableHash(id)}`,
		};
		this.store.mutate((state) => {
			state.invites.unshift(invite);
			state.members.push({
				id: `member-${state.sequence++}`,
				tenantId: scope.tenantId,
				email: input.email,
				name: input.email.split("@")[0] ?? input.email,
				role: input.role,
				status: "INVITED",
				lastActiveAt: null,
				joinedAt: invite.createdAt,
			});
		});
		this.audit("invite.create", "INVITE", id, `${input.email} を招待`);
		return invite;
	}

	async revokeInvite(scope: Scope, inviteId: string): Promise<void> {
		this.assertTenantAdmin(scope);
		await this.delay();
		this.store.mutate((state) => {
			const target = state.invites.find((i) => i.id === inviteId);
			if (target) target.status = "REVOKED";
		});
		this.audit("invite.revoke", "INVITE", inviteId, "招待を取り消し");
	}

	async listRoleDefinitions(scope: Scope): Promise<RoleDefinition[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return seed.roleDefinitions;
	}

	async listCatalogEntitlements(scope: Scope): Promise<TenantCatalogEntitlement[]> {
		this.assertScope(scope);
		await this.delay();
		return this.store.read().catalogEntitlements;
	}

	async setCatalogEntitlement(scope: Scope, ucsId: string, enabled: boolean) {
		this.assertTenantAdmin(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const target = state.catalogEntitlements.find((e) => e.ucsId === ucsId);
			if (!target) throw ApiError.notFound("UCS entitlement not found");
			const publication = state.publications.find((p) => p.ucsId === ucsId);
			if (enabled && publication?.status !== "PUBLISHED") {
				throw ApiError.forbidden(
					"未公開のUCSはTenantで有効化できません。",
					ReasonCodes.ASSIGNMENT_DENIED,
				);
			}
			target.enabled = enabled;
			target.restrictedReason = enabled ? null : "Tenant Adminがこの業種UCSを有効化していません。";
			target.allowedRoles = enabled ? ["tenant_member", "tenant_admin"] : [];
			return { ...target };
		});
		this.audit("entitlement.update", "UCS", ucsId, enabled ? "利用を許可" : "利用を停止");
		return updated;
	}

	async listPoolAssignments(scope: Scope): Promise<PoolAssignment[]> {
		this.assertScope(scope);
		await this.delay();
		return this.store.read().poolAssignments;
	}

	async setPoolAssignment(
		scope: Scope,
		input: { poolId: string; subjectId: string; effect: "ALLOW" | "DENY" },
	): Promise<PoolAssignment> {
		this.assertTenantAdmin(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const existing = state.poolAssignments.find(
				(a) => a.poolId === input.poolId && a.subjectId === input.subjectId,
			);
			if (existing) {
				existing.effect = input.effect;
				existing.allowedPurposes = input.effect === "ALLOW" ? ["CODING", "DESIGN", "DOCUMENT"] : [];
				return { ...existing };
			}
			const created: PoolAssignment = {
				id: `assign-${state.sequence++}`,
				poolId: input.poolId,
				poolAlias:
					input.poolId === "pool-claude-coding" ? "Claude Coding Standard" : "Standard Assist",
				providerId: input.poolId === "pool-claude-coding" ? "provider-anthropic" : "provider-fake",
				subjectType: "USER",
				subjectId: input.subjectId,
				subjectLabel: userLabel(input.subjectId),
				effect: input.effect,
				allowedPurposes: input.effect === "ALLOW" ? ["CODING", "DESIGN", "DOCUMENT"] : [],
				validFrom: this.iso(),
				validUntil: null,
			};
			state.poolAssignments.push(created);
			return created;
		});
		this.audit(
			`pool_assignment.${input.effect.toLowerCase()}`,
			"SYSTEM_AUTH_POOL",
			input.poolId,
			`${userLabel(input.subjectId)} の利用可否を更新`,
		);
		return updated;
	}

	async getByoPolicy(scope: Scope): Promise<ByoPolicy> {
		this.assertScope(scope);
		await this.delay();
		return this.store.read().byoPolicy;
	}

	async setByoPolicy(scope: Scope, input: { byokAllowed: boolean; byosAllowed: boolean }) {
		this.assertTenantAdmin(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			const policy = state.byoPolicy;
			// Platform Hard Limitより広い範囲をTenantが許可することはできない（design §21.6）。
			if (input.byokAllowed && !policy.platformByokAllowed) {
				throw ApiError.forbidden(
					"Platform PolicyがBYOKを禁止しているため許可できません。",
					ReasonCodes.PROVIDER_POLICY_DENIED,
				);
			}
			if (input.byosAllowed && !policy.platformByosAllowed) {
				throw ApiError.forbidden(
					"Platform PolicyがBYOSを禁止しているため許可できません。",
					ReasonCodes.PROVIDER_POLICY_DENIED,
				);
			}
			policy.byokAllowed = input.byokAllowed;
			policy.byosAllowed = input.byosAllowed;
			return { ...policy };
		});
		this.audit("byo_policy.update", "TENANT", scope.tenantId, "BYOK/BYOS許可設定を更新");
		return updated;
	}

	async listProviderUsageRules(scope: Scope): Promise<ProviderUsageRule[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return seed.providerUsageRules;
	}

	async listBudgets(scope: Scope): Promise<Budget[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return this.store.read().budgets.filter((b) => b.tenantId === scope.tenantId);
	}

	async updateBudget(scope: Scope, budgetId: string, limitAmount: number): Promise<Budget> {
		this.assertTenantAdmin(scope);
		await this.delay();
		if (limitAmount < 0) throw ApiError.validation("上限金額は0以上で指定してください。");
		const updated = this.store.mutate((state) => {
			const target = state.budgets.find((b) => b.id === budgetId);
			if (!target) throw ApiError.notFound("Budget not found");
			target.limitAmount = limitAmount;
			return { ...target };
		});
		this.audit("budget.update", "BUDGET", budgetId, `上限を${limitAmount}へ変更`);
		return updated;
	}

	async listIntegrations(scope: Scope): Promise<Integration[]> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return this.store.read().integrations.filter((i) => i.tenantId === scope.tenantId);
	}

	async getTenantSettings(scope: Scope): Promise<TenantSettings> {
		this.assertTenantAdmin(scope);
		await this.delay();
		return this.store.read().tenantSettings;
	}

	async updateTenantSettings(scope: Scope, input: Partial<TenantSettings>) {
		this.assertTenantAdmin(scope);
		await this.delay();
		const updated = this.store.mutate((state) => {
			Object.assign(state.tenantSettings, input);
			return { ...state.tenantSettings };
		});
		this.audit("tenant_settings.update", "TENANT", scope.tenantId, "Tenant設定を更新");
		return updated;
	}

	// -- system admin -------------------------------------------------------------

	async listTenants(): Promise<TenantSummary[]> {
		this.assertSystemAdmin();
		await this.delay();
		return seed.tenantSummaries;
	}

	async getTenantSummary(tenantId: string): Promise<TenantSummary> {
		this.assertSystemAdmin();
		await this.delay();
		const found = seed.tenantSummaries.find((t) => t.id === tenantId);
		if (!found) throw ApiError.notFound("Tenant not found");
		return found;
	}

	async listAllUsers(): Promise<TenantMember[]> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().members;
	}

	async listPublications(): Promise<CatalogPublication[]> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().publications;
	}

	async setPublicationStatus(ucsId: string, status: CatalogPublication["status"]) {
		this.assertSystemAdmin();
		await this.delay();
		const updated = this.store.mutate((state) => {
			const target = state.publications.find((p) => p.ucsId === ucsId);
			if (!target) throw ApiError.notFound("UCS not found");
			target.status = status;
			target.publishedAt = status === "PUBLISHED" ? this.iso() : null;
			if (status !== "PUBLISHED") {
				// 公開停止したUCSはTenant側の有効化も落とす。上位が下位を狭める方向のみ許す。
				const entitlement = state.catalogEntitlements.find((e) => e.ucsId === ucsId);
				if (entitlement) {
					entitlement.enabled = false;
					entitlement.restrictedReason = "公開が停止されたUCSです。";
					entitlement.allowedRoles = [];
				}
			}
			return { ...target };
		});
		this.audit("publication.update", "UCS", ucsId, `公開状態を${status}へ変更`);
		return updated;
	}

	async listPlans(): Promise<Plan[]> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().plans;
	}

	async updatePlan(planId: string, input: Partial<Plan>): Promise<Plan> {
		this.assertSystemAdmin();
		await this.delay();
		const updated = this.store.mutate((state) => {
			const target = state.plans.find((p) => p.id === planId);
			if (!target) throw ApiError.notFound("Plan not found");
			Object.assign(target, input);
			return { ...target };
		});
		this.audit("plan.update", "PLAN", planId, "Planを更新");
		return updated;
	}

	async listNotices(): Promise<Notice[]> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().notices;
	}

	async upsertNotice(input: Partial<Notice> & { title: string; body: string }): Promise<Notice> {
		this.assertSystemAdmin();
		await this.delay();
		const result = this.store.mutate((state) => {
			if (input.id) {
				const target = state.notices.find((n) => n.id === input.id);
				if (!target) throw ApiError.notFound("Notice not found");
				Object.assign(target, input);
				if (target.status === "PUBLISHED" && !target.publishedAt) target.publishedAt = this.iso();
				return { ...target };
			}
			const created: Notice = {
				id: `notice-${state.sequence++}`,
				title: input.title,
				body: input.body,
				category: input.category ?? "RELEASE",
				status: input.status ?? "DRAFT",
				publishedAt: input.status === "PUBLISHED" ? this.iso() : null,
				audience: input.audience ?? "ALL",
			};
			state.notices.unshift(created);
			return created;
		});
		this.audit("notice.upsert", "NOTICE", result.id, "お知らせを保存");
		return result;
	}

	async listContactMessages(): Promise<ContactMessage[]> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().contactMessages;
	}

	async updateContactStatus(id: string, status: ContactMessage["status"]) {
		this.assertSystemAdmin();
		await this.delay();
		const updated = this.store.mutate((state) => {
			const target = state.contactMessages.find((c) => c.id === id);
			if (!target) throw ApiError.notFound("Contact not found");
			target.status = status;
			return { ...target };
		});
		this.audit("contact.update", "CONTACT", id, `対応状況を${status}へ変更`);
		return updated;
	}

	async getProductAnalytics() {
		this.assertSystemAdmin();
		await this.delay();
		const state = this.store.read();
		return {
			tenants: seed.tenantSummaries.length,
			activeUsers: state.members.filter((m) => m.status === "ACTIVE").length,
			executions: state.executions.length,
			artifacts: state.artifacts.length,
			revenue: seed.tenantSummaries.reduce((s, t) => s + t.monthlyCost, 0),
			series: [
				{ label: "2026-05", executions: 18, revenue: 52000 },
				{ label: "2026-06", executions: 34, revenue: 71400 },
				{ label: "2026-07", executions: 51, revenue: 88200 },
				{ label: "2026-08", executions: state.executions.length, revenue: 96650 },
			],
		};
	}

	async getProductSettings(): Promise<ProductSettings> {
		this.assertSystemAdmin();
		await this.delay();
		return this.store.read().productSettings;
	}

	async updateProductSettings(input: Partial<ProductSettings>): Promise<ProductSettings> {
		this.assertSystemAdmin();
		await this.delay();
		const updated = this.store.mutate((state) => {
			Object.assign(state.productSettings, input);
			return { ...state.productSettings };
		});
		this.audit("product_settings.update", "PRODUCT", "agentry", "製品設定を更新");
		return updated;
	}
}

function roleLabel(role: WorkOrderSessionRole): string {
	return role === "design" ? "設計" : role === "implement" ? "実装" : "受け入れ";
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * 読み取りは常にCopyを返す。
 * 内部StateへのReferenceを画面へ渡すと、後続のmutateが過去に取得した値まで
 * 書き換えてしまい「取得済みの状態」と比較できなくなる。
 */
function snapshot<T>(value: T): T {
	return JSON.parse(JSON.stringify(value)) as T;
}

function emptyWallClock() {
	return {
		totalSeconds: 0,
		queuedSeconds: 0,
		wakeSeconds: 0,
		prepareSeconds: 0,
		llmSeconds: 0,
		ciSeconds: 0,
		integrateSeconds: 0,
		deploySeconds: 0,
		evidenceWaitSeconds: 0,
		acceptSeconds: 0,
	};
}

function readStoredAuth(): AuthSession | null {
	try {
		const raw = localStorage?.getItem(SESSION_KEY);
		if (!raw) return null;
		const value: unknown = JSON.parse(raw);
		const bootstrap = SessionBootstrapSchema.safeParse(value);
		const auth = bootstrap.success ? bootstrap : AuthSessionSchema.safeParse(value);
		if (auth.success) return auth.data;
		localStorage?.removeItem(SESSION_KEY);
		return null;
	} catch {
		try {
			localStorage?.removeItem(SESSION_KEY);
		} catch {
			/* storage unavailable */
		}
		return null;
	}
}

function agentNameOf(agentId: string): string {
	return seed.agents.find((a) => a.id === agentId)?.displayName ?? "Agent";
}

function userLabel(userId: string): string {
	return seed.users.find((u) => u.id === userId)?.name ?? userId;
}

function verticalPackageFor(ucsId: string) {
	const ucs = seed.ucsList.find((u) => u.id === ucsId);
	if (!ucs?.verticalKey) return undefined;
	return seed.verticalPackages.find((v) => v.key === ucs.verticalKey);
}

export function isTerminal(status: WorkflowExecution["status"]): boolean {
	return (
		status === "SUCCEEDED" ||
		status === "FAILED" ||
		status === "CANCELLED" ||
		status === "TIMED_OUT"
	);
}

function buildTimeline(execution: WorkflowExecution): ExecutionTimeline {
	const definition = seed.workflowDefinitions.find((d) => d.id === execution.workflowDefinitionId);
	const nodes = definition?.nodes ?? [];
	const waitingIndex = nodes.findIndex((n) => n.nodeType === "APPROVAL");
	const cutoff =
		execution.status === "SUCCEEDED"
			? nodes.length
			: execution.status === "FAILED"
				? Math.max(1, Math.floor(nodes.length / 2))
				: waitingIndex >= 0
					? waitingIndex
					: Math.floor(nodes.length / 2);

	const nodeExecutions = nodes.map((node, i) => ({
		id: `${execution.id}-node-${node.nodeId}`,
		nodeId: node.nodeId,
		nodeType: node.nodeType,
		status:
			i < cutoff
				? "SUCCEEDED"
				: i === cutoff
					? execution.status === "FAILED"
						? "FAILED"
						: execution.status === "WAITING"
							? "WAITING"
							: "RUNNING"
					: "PENDING",
		waitReason: i === cutoff && execution.status === "WAITING" ? execution.waitReason : null,
		currentAttemptNo: i <= cutoff ? 1 : 0,
		startedAt: i <= cutoff ? execution.createdAt : null,
		endedAt: i < cutoff ? execution.updatedAt : null,
	}));

	const attempts = nodeExecutions
		.filter((n) => n.currentAttemptNo > 0)
		.map((n, i) => ({
			id: `${n.id}-attempt-1`,
			nodeExecutionId: n.id,
			attemptNo: 1,
			status: n.status === "FAILED" ? "FAILED" : n.status === "SUCCEEDED" ? "SUCCEEDED" : "RUNNING",
			errorClass: n.status === "FAILED" ? execution.failureClass : null,
			startedAt: execution.createdAt,
			endedAt: n.status === "SUCCEEDED" ? execution.updatedAt : null,
			_i: i,
		}))
		.map(({ _i, ...rest }) => rest);

	const dynamicSubtasks = nodeExecutions
		.filter((n) => n.nodeType === "AGENT_TASK" && n.status !== "PENDING")
		.flatMap((n, i) => [
			{
				id: `${n.id}-subtask-1`,
				nodeExecutionId: n.id,
				objective: `${n.nodeId} の入力整理`,
				status: "SUCCEEDED",
				sequence: i * 2,
				createdReason: "AGENT_PLAN",
			},
			{
				id: `${n.id}-subtask-2`,
				nodeExecutionId: n.id,
				objective: `${n.nodeId} の出力検証`,
				status: n.status === "SUCCEEDED" ? "SUCCEEDED" : "RUNNING",
				sequence: i * 2 + 1,
				createdReason: "AGENT_REPLAN",
			},
		]);

	const transitions = nodeExecutions
		.filter((n) => n.status !== "PENDING")
		.map((n, i) => ({
			id: `${execution.id}-tr-${i}`,
			entityType: "NODE",
			entityId: n.id,
			fromStatus: i === 0 ? null : "READY",
			toStatus: n.status,
			reasonCode: n.status === "FAILED" ? (execution.failureClass ?? "UNKNOWN") : "NODE_COMPLETED",
			actorId: "system",
			occurredAt: execution.updatedAt,
		}));

	return {
		execution,
		waitHandles: [],
		nodes: nodeExecutions,
		attempts,
		dynamicSubtasks,
		transitions,
	};
}

function buildProgressEvents(execution: WorkflowExecution): ProgressEvent[] {
	const timeline = buildTimeline(execution);
	const base = {
		schemaVersion: PROGRESS_EVENT_SCHEMA_VERSION,
		tenantId: execution.tenantId,
		executionId: execution.id,
		sessionId: execution.sessionId,
		correlationId: `corr-${execution.id}`,
		traceId: `trace-${execution.id}`,
	} as const;

	const events: ProgressEvent[] = [
		{
			...base,
			eventId: `${execution.id}-e0`,
			sequence: 0,
			eventType: "EXECUTION_STARTED",
			entityType: "WORKFLOW",
			entityId: execution.id,
			fromStatus: null,
			toStatus: "RUNNING",
			message: "Workflowを開始しました。",
			occurredAt: execution.createdAt,
		},
	];

	let seq = 1;
	for (const node of timeline.nodes) {
		if (node.status === "PENDING") continue;
		events.push({
			...base,
			eventId: `${execution.id}-e${seq}`,
			sequence: seq,
			eventType: "NODE_STATUS_CHANGED",
			entityType: "NODE",
			entityId: node.id,
			fromStatus: "READY",
			toStatus: node.status,
			message: `${node.nodeId}: ${node.status}`,
			occurredAt: execution.updatedAt,
		});
		seq += 1;
	}

	if (execution.status === "WAITING") {
		events.push({
			...base,
			eventId: `${execution.id}-e${seq}`,
			sequence: seq,
			eventType: "WAIT_STARTED",
			entityType: "WORKFLOW",
			entityId: execution.id,
			fromStatus: "RUNNING",
			toStatus: "WAITING",
			message: `承認待ちになりました (${execution.waitReason ?? "USER_APPROVAL"})。`,
			occurredAt: execution.updatedAt,
		});
		seq += 1;
	}

	if (isTerminal(execution.status)) {
		events.push({
			...base,
			eventId: `${execution.id}-e${seq}`,
			sequence: seq,
			eventType: "EXECUTION_COMPLETED",
			entityType: "WORKFLOW",
			entityId: execution.id,
			fromStatus: "RUNNING",
			toStatus: execution.status,
			message: `Workflowが${execution.status}で終了しました。`,
			occurredAt: execution.endedAt ?? execution.updatedAt,
		});
	}

	return events;
}
